/**
 * unreadIndicator — badges the browser tab's favicon with a colored dot.
 *
 * Two independent indicators, both drawn over the original favicon:
 * - "running" (amber): shown for the whole duration of an async chat request,
 *   regardless of tab focus. Not affected by visibility changes.
 * - "unread reply" (green): shown once a reply arrives while the tab isn't
 *   focused, cleared as soon as the tab regains focus.
 */

let originalFaviconHref = null;
const badgedFaviconHrefCache = {};

const getFaviconLink = () => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
};

const drawBadgedFavicon = (baseHref, color) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        ctx.beginPath();
        ctx.arc(size - 7, 7, 7, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        // Canvas can throw (e.g. a tainted cross-origin favicon) — fail silently,
        // this indicator is a non-critical enhancement.
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = baseHref;
  });

const showBadge = async (key, color) => {
  const link = getFaviconLink();
  if (!originalFaviconHref) originalFaviconHref = link.href;

  if (!badgedFaviconHrefCache[key]) {
    badgedFaviconHrefCache[key] = await drawBadgedFavicon(originalFaviconHref, color);
  }
  if (badgedFaviconHrefCache[key]) link.href = badgedFaviconHrefCache[key];
};

const restoreOriginalFavicon = () => {
  if (!originalFaviconHref) return;
  getFaviconLink().href = originalFaviconHref;
};

export const showRunningIndicator = () => showBadge("running", "#f59e0b");
export const clearRunningIndicator = restoreOriginalFavicon;

export const showUnreadReplyBadge = () => showBadge("unread", "#10b981");
export const clearUnreadReplyBadge = restoreOriginalFavicon;
